import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TalentDashboardPage.module.css';
import TalentContentContainer from '../layout/TalentContentContainer';
import { IcoNotiBell } from './components/DashboardIcons';
import MessageModal from './components/MessageModal';
import StatsCard from './components/StatsCard';
import MiniCalendar from './components/MiniCalendar';
import SectionHeader from './components/SectionHeader';
import RecommendedPostings from './components/RecommendedPostings/RecommendedPostings';
import { C, t } from './components/dashboardConstants';
import {
  getMyApplications,
  getNotifications,
  getSchedules,
  getCastingList,
  getTalentDetail,
} from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import { getScheduleAccentBackground, normalizeScheduleColor } from '../../../shared/schedule/scheduleColors';
import EventDetailModal from '../../../shared/schedule/EventDetailModal';
import DayEventsModal from '../../../shared/schedule/DayEventsModal';

const STATUS_LABELS = {
  SUBMITTED: '지원 완료',
  PASS_1: '1차 통과',
  PASS_2: '2차 통과',
  PASS_3: '3차 통과',
  PASS_4: '4차 통과',
  HIRED: '합격',
};

// 백엔드 enum이 그대로 노출될 경우를 위한 폴백 변환 맵
const STEP_ENUM_LABELS = {
  STEP_SCREENING_PASS: '서류 통과',
  STEP_CALLBACK: '콜백',
  STEP_FINAL: '최종 합격',
  PASS_1: '1차 통과',
  PASS_2: '2차 통과',
  PASS_3: '3차 통과',
  PASS_4: '4차 통과',
  HIRED: '합격',
  SUBMITTED: '지원 완료',
  REJECTED: '불합격',
};

// 알림 메시지 표시: currentStageName/stepName 우선, 없으면 enum 변환, 없으면 원문 그대로
function formatNotiMessage(n) {
  let msg = n.message || n.content || n.title || '알림';
  const stageName = n.currentStageName || n.stepName || null;
  // enum 값 변환 (currentStageName 우선, 없으면 맵 사용)
  if (stageName) {
    msg = msg.replace(/STEP_\w+|PASS_\d+|HIRED|SUBMITTED|REJECTED/g, stageName);
  } else {
    msg = msg.replace(/STEP_\w+|PASS_\d+|HIRED|SUBMITTED|REJECTED/g, (match) => STEP_ENUM_LABELS[match] || match);
  }
  // 괄호로 묶인 부가 설명 제거: " (1차 공고)", " (서류 단계)" 등
  msg = msg.replace(/\s*\([^)]*공고[^)]*\)/g, '').replace(/\s*\([^)]*단계[^)]*\)/g, '').trim();
  return msg;
}

function dday(dateStr) {
  if (!dateStr) return '';
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'D-Day';
  if (diff > 0) return `D-${diff}`;
  return `D+${Math.abs(diff)}`;
}

function formatScheduleDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16).replace('T', ' ');
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function CompactAuditionCard({ casting, navigate }) {
  if (!casting) {
    return (
      <div style={{ border: `1px solid ${C.g200}`, borderRadius: '12px', padding: '14px 16px', background: C.white, height: '80px' }}>
        <div style={{ height: 14, width: '60%', background: C.g200, borderRadius: 4 }} />
      </div>
    );
  }
  const badge = dday(casting.applicationDeadline);
  const badgeColor = /^D-(Day|[1-3])$/.test(badge) ? '#EF4444' : /^D-[1-9]$/.test(badge) ? '#FF7A00' : C.g400;
  return (
    <div
      onClick={() => navigate(`/castings/${casting.id}`, { state: { backLabel: '대시보드로 돌아가기' } })}
      style={{ border: `1px solid ${C.g200}`, borderRadius: '12px', padding: '14px 16px', background: C.white, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '6px', overflow: 'hidden', minWidth: 0 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {casting.projectType && <span style={{ background: C.point, borderRadius: '4px', padding: '2px 6px', ...t('11px', 600, C.bk) }}>{casting.projectType}</span>}
        {badge && <span style={t('12px', 500, badgeColor)}>{badge}</span>}
      </div>
      <span style={{ ...t('14px', 600, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', minWidth: 0 }}>{casting.title}</span>
      {casting.producerName && <span style={t('12px', 400, C.g400)}>{casting.producerName}</span>}
    </div>
  );
}

export default function TalentDashboardPage() {
  const navigate = useNavigate();
  const { profileId, userName } = useAuth();
  const [showMsg, setShowMsg] = useState(false);
  const [stageStatus, setStageStatus] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [recentCastings, setRecentCastings] = useState([]);
  const [calendarMarked, setCalendarMarked] = useState([]);
  const [detailEvent, setDetailEvent] = useState(null);
  const [dayModal, setDayModal] = useState(null);

  const fetchCalendarSchedules = useCallback((year, month) => {
    const _pad = (n) => String(n).padStart(2, '0');
    const _toDateStr = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    const start = new Date(monthStart);
    start.setDate(monthStart.getDate() - monthStart.getDay());
    const end = new Date(monthEnd);
    end.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
    getSchedules(_toDateStr(start), _toDateStr(end))
      .then(list => {
        const arr = Array.isArray(list) ? list : (list?.content ?? []);
        const dates = arr.map((s) => {
          const raw = s.eventDate || s.date || s.scheduledAt || s.startAt;
          if (!raw) return null;
          return {
            date: String(raw).slice(0, 10),
            color: normalizeScheduleColor(s.color || s.eventColor),
          };
        }).filter(Boolean);
        setCalendarMarked(dates);
      })
      .catch(() => {});
  }, []);

  const loadDashboardData = useCallback(() => {
    const now = new Date();
    const _pad = (n) => String(n).padStart(2, '0');
    const _toDateStr = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;

    fetchCalendarSchedules(now.getFullYear(), now.getMonth());

    Promise.all([
      profileId ? getTalentDetail(profileId).catch(() => null) : Promise.resolve(null),
      getMyApplications().catch(() => null),
      getNotifications().catch(() => []),
      getSchedules(_toDateStr(now), _toDateStr(new Date(now.getFullYear(), now.getMonth() + 3, 0))).catch(() => []),
      getCastingList({ status: 'OPEN', perpage: 4, callpage: 1 }).catch(() => null),
    ]).then(([profileData, applicationsRes, notiList, schedules, castingData]) => {
      const applicationList = Array.isArray(applicationsRes)
        ? applicationsRes
        : (applicationsRes?.content ?? []);

      const counts = {};
      applicationList.forEach((app) => {
        if (app.status in STATUS_LABELS) {
          counts[app.status] = (counts[app.status] || 0) + 1;
        }
      });
      setStageStatus(
        Object.entries(STATUS_LABELS).map(([key, label]) => ({
          label,
          count: counts[key] || 0,
        }))
      );

      setNotifications(notiList || []);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const list = Array.isArray(schedules) ? schedules : (schedules?.content ?? []);
      const getDate = (s) => new Date(s.eventDate || s.date || s.scheduledAt || s.startAt || 0);
      setUpcomingSchedules(list.filter((s) => getDate(s) >= today).sort((a, b) => getDate(a) - getDate(b)));

      const castings = Array.isArray(castingData) ? castingData : (castingData?.content ?? []);
      const now = new Date();
      const activeCastings = castings.filter(c => {
        if (!c.applicationDeadline) return true;
        return new Date(c.applicationDeadline) >= now;
      });
      setRecentCastings(activeCastings.slice(0, 4));
    });
  }, [profileId, fetchCalendarSchedules]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadDashboardData();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadDashboardData]);

  return (
    <div className={styles.pageRoot}>
      <TalentContentContainer>
        {/* Top header */}
        <div className={styles.header}>
          <div className={styles.headerInner}>
            <div className={styles.headerLeft}>
              <h1 className={styles.headerTitle}>배우 대시보드</h1>
              <p className={styles.headerSubtitle}>
                {userName ? `안녕하세요, ${userName}님. ` : ''}오늘도 좋은 기회가 기다리고 있어요.
              </p>
            </div>
            {/* <div className={styles.chatBtn} onClick={() => setShowMsg(true)}>
              <IcoChat c="#090909" />
              <div className={styles.chatBadge}>
                <span className={styles.chatBadgeText}>2</span>
              </div>
            </div> */}
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <StatsCard />

          <div className={styles.columns}>

            {/* Left column */}
            <div className={styles.leftCol}>

              {/* 나에게 맞는 공고 — 쓸 수 없는 계정이면 스스로 숨는다 */}
              <RecommendedPostings />

              {/* 최근 공고 - 2x2 grid */}
              <div>
                <SectionHeader title="최근 공고" onViewAll={() => navigate('/castings')} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {recentCastings.length > 0
                    ? recentCastings.map((casting) => (
                        <CompactAuditionCard key={casting.id} casting={casting} navigate={navigate} />
                      ))
                    : [0, 1, 2, 3].map((i) => <CompactAuditionCard key={i} navigate={navigate} />)
                  }
                </div>
              </div>

              {/* 다가오는 일정 - 리스트만 (캘린더 없음) */}
              <div>
                <SectionHeader title="다가오는 일정" onViewAll={() => navigate('/talent/schedule')} />
                <div className={styles.scheduleList}>
                  {upcomingSchedules.length > 0
                    ? upcomingSchedules.slice(0, 7).map((item, i, arr) => {
                        const badge = dday(item.eventDate || item.date || item.scheduledAt || item.startAt);
                        const scheduleColor = normalizeScheduleColor(item.color || item.eventColor);
                        const bg = getScheduleAccentBackground(scheduleColor);
                        return (
                          <div
                            key={i}
                            className={i < arr.length - 1 ? styles.scheduleItemBordered : styles.scheduleItem}
                            onClick={() => setDetailEvent(item)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span className={styles.scheduleTitle}>{item.title}</span>
                            <div className={styles.scheduleRight}>
                              <span className={styles.scheduleMetaInline}>
                                {formatScheduleDate(item.eventDate || item.date || item.scheduledAt || item.startAt)} {item.time || ''}
                              </span>
                              <span style={{ background: bg, border: `1px solid ${scheduleColor}`, borderRadius: '6px', padding: '4px 10px', fontFamily: 'Pretendard, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '-0.04em', color: scheduleColor, lineHeight: '140%' }}>{badge}</span>
                            </div>
                          </div>
                        );
                      })
                    : (
                      <div className={styles.scheduleEmpty}>
                        <span className={styles.emptyText}>다가오는 일정이 없습니다.</span>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className={styles.rightCol}>

              {/* 지원 단계별 현황 */}
              <div>
                <SectionHeader title="지원 단계별 현황" onViewAll={() => navigate('/talent/applications')} />
                <div className={styles.stageCard}>
                  {stageStatus.map((s, i) => (
                    <div key={i} className={styles.stageRow}>
                      <span className={styles.stageLabel}>{s.label}</span>
                      <span className={styles.stageCount}>{s.count}건</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 최근 알림 */}
              <div>
                <SectionHeader title="최근 알림" onViewAll={() => navigate('/talent/applications')} />
                <div className={styles.notiCard}>
                  {notifications.length > 0
                    ? notifications.slice(0, 3).map((n, i) => (
                        <div key={i} className={styles.notiItem}>
                          <div className={styles.notiIcon}>
                            <IcoNotiBell />
                          </div>
                          <div className={styles.notiBody}>
                            <div className={styles.notiMessage}>{formatNotiMessage(n)}</div>
                            <div className={styles.notiTime}>
                              {n.createdAt ? new Date(n.createdAt).toLocaleString('ko-KR') : ''}
                            </div>
                          </div>
                        </div>
                      ))
                    : (
                      <div className={styles.notiEmpty}>
                        <span className={styles.emptyText}>알림이 없습니다.</span>
                      </div>
                    )
                  }
                </div>
              </div>

              {/* 캘린더 */}
              <div>
                <SectionHeader title="캘린더" onViewAll={() => navigate('/talent/schedule')} />
                <div className={styles.calendarBox}>
                <MiniCalendar
                  markedDates={calendarMarked}
                  onMonthChange={(y, m) => fetchCalendarSchedules(y, m)}
                  onDateClick={(date, events) => {
                    if (events.length > 0) setDayModal({ date, events });
                  }}
                />
              </div>
              </div>

            </div>
          </div>
        </div>
      </TalentContentContainer>
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
        />
      )}
      {dayModal && (
        <DayEventsModal
          date={dayModal.date}
          events={dayModal.events}
          onClose={() => setDayModal(null)}
        />
      )}
      {showMsg && <MessageModal onClose={() => setShowMsg(false)} />}
    </div>
  );
}
